import React from "react";
import LegalPage from "../../components/LegalPage";

export default function WebsiteTerms() {
  return (
    <LegalPage
      testid="legal-website-terms"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Website Terms of Use"
      lastUpdated="14 May 2026 · Version 1.1 (Global)"
      intro="These Website Terms of Use (the “Website Terms”) govern your access to and use of blueroam.io and any other website, sub-domain or web application operated by BlueRoam Ltd (“BlueRoam”, “we”, “us”, “our”) (the “Website”)."
    >
      <p>
        These Website Terms are separate from, and in addition to, our{" "}
        <a href="/legal/terms">Terms of Service</a> (governing purchase and use of our eSIM
        products), <a href="/legal/refunds">Refund Policy</a>,{" "}
        <a href="/legal/privacy">Privacy Policy</a>, US Privacy Notice (where applicable) and{" "}
        <a href="/legal/aml-kyc">AML/KYC Policy</a>. Where the Terms of Service conflict with these
        Website Terms in relation to a purchase, the Terms of Service prevail.
      </p>
      <p>
        <strong>
          By accessing or using the Website, you agree to be bound by these Website Terms, wherever
          you are located. If you do not agree, please do not use the Website.
        </strong>
      </p>

      <h2>1. About Us</h2>
      <p>
        BlueRoam Ltd is registered in England and Wales (company number 17215030), registered office
        128 City Road, London, EC1V 2NX. Contact:{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.
      </p>

      <h2>2. Changes to the Website and These Terms</h2>
      <p>2.1 We may modify, suspend or discontinue any part of the Website at any time, with or without notice.</p>
      <p>2.2 We may update these Website Terms from time to time. The version in force at the time you use the Website applies to that visit.</p>

      <h2>3. Accessing the Website</h2>
      <p>3.1 The Website is provided <strong>free of charge</strong> for general informational and transactional purposes.</p>
      <p>3.2 Access is permitted on a temporary basis; we do not guarantee that the Website will always be available or uninterrupted.</p>
      <p>3.3 You are responsible for ensuring that all persons who access the Website through your internet connection comply with these Website Terms.</p>
      <p>3.4 <strong>Geographic availability.</strong> The Website and Services are offered worldwide except in:</p>
      <ul>
        <li>(a) <strong>comprehensively sanctioned jurisdictions</strong> (currently Cuba, Iran, North Korea, Syria and the Russian-occupied regions of Ukraine);</li>
        <li>(b) any other country where applicable law prevents us from offering the Services.</li>
      </ul>
      <p>
        We do not represent that the Website or its content is appropriate or available in every
        location, and we may limit availability to any person or geographic area at any time.
      </p>

      <h2>4. Your Account</h2>
      <p>4.1 If you create an account, you must provide accurate, complete information and keep it up to date. You are responsible for safeguarding your credentials and all activity under your account.</p>
      <p>4.2 Notify us immediately at <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> of any unauthorised use or security breach.</p>
      <p>4.3 We may suspend or terminate your account where we reasonably believe you have breached these Website Terms or our Terms of Service.</p>

      <h2>5. Acceptable Use</h2>
      <p>5.1 You must not use the Website:</p>
      <ul>
        <li>(a) in breach of any applicable local, national or international law (including UK, EU, US and the laws of your country of residence);</li>
        <li>(b) for any unlawful or fraudulent purpose;</li>
        <li>(c) to harm or attempt to harm minors;</li>
        <li>(d) to transmit unsolicited advertising or promotional material (spam) in breach of CAN-SPAM, CASL, the UK/EU ePrivacy regimes or equivalent laws;</li>
        <li>(e) to knowingly transmit malware or any harmful code;</li>
        <li>(f) to attempt unauthorised access to the Website or any connected system;</li>
        <li>(g) to attack the Website via denial-of-service or similar;</li>
        <li>(h) to scrape, harvest or extract data except as permitted by our robots.txt or with our prior written consent;</li>
        <li>(i) to reverse engineer, decompile or disassemble any software, except as permitted by law;</li>
        <li>(j) to misrepresent your identity.</li>
      </ul>
      <p>
        5.2 Breach may constitute a criminal offence under the UK Computer Misuse Act 1990, the US
        Computer Fraud and Abuse Act or equivalent laws. We will report breaches to law enforcement
        and cooperate, including by disclosing your identity where lawful.
      </p>

      <h2>6. Intellectual Property</h2>
      <p>6.1 We own or licence all intellectual property in the Website, including the <strong>BlueRoam</strong> name and logo, all text, graphics, software, design and UI elements. These are protected by copyright, trade mark and database rights worldwide.</p>
      <p>6.2 You may print one copy and download extracts for <strong>personal use</strong>, and may draw the attention of others within your organisation to content posted on the Website.</p>
      <p>6.3 You must not modify printed or digital copies; must not use illustrations, photos, video or audio separately from accompanying text; and must acknowledge our (and contributors’) authorship.</p>
      <p>6.4 No commercial use without a licence from us or our licensors.</p>
      <p>6.5 Breach of this clause ends your right to use the Website immediately; you must, at our option, return or destroy any copies.</p>

      <h2>7. No Reliance on Information</h2>
      <p>7.1 Website content is for <strong>general information only</strong>. It does not constitute advice on which you should rely. Obtain professional or specialist advice before acting or refraining from action.</p>
      <p>7.2 We make no representations or warranties, express or implied, that content is accurate, complete or up to date.</p>

      <h2>8. Linking and Third-Party Content</h2>
      <p>8.1 You may link to our home page on terms that are fair and legal and that do not damage our reputation.</p>
      <p>8.2 No link suggesting association, approval or endorsement where none exists. No framing without our consent. No deep linking other than to the home page without prior written consent.</p>
      <p>8.3 Where the Website links to third-party sites, those links are for information only. We have no control over their contents and accept no responsibility.</p>

      <h2>9. Disclaimer and Limitation of Liability</h2>
      <p>9.1 To the extent permitted by law, we exclude all conditions, warranties, representations and other terms that may apply to the Website or its content, express or implied.</p>
      <p>9.2 Nothing in these Website Terms excludes or limits liability for:</p>
      <ul>
        <li>(a) death or personal injury caused by negligence;</li>
        <li>(b) fraud or fraudulent misrepresentation; or</li>
        <li>(c) any other liability that cannot be excluded or limited under applicable law (including statutory rights under the UK Consumer Rights Act 2015 and equivalent foreign consumer-protection legislation).</li>
      </ul>
      <p>9.3 To the extent permitted by law, we are not liable for any loss or damage, whether in contract, tort, breach of statutory duty or otherwise, arising under or in connection with:</p>
      <ul>
        <li>(a) use of, or inability to use, the Website; or</li>
        <li>(b) use of, or reliance on, content displayed on the Website.</li>
      </ul>
      <p>9.4 <strong>Business users</strong> in particular: we are not liable for loss of profits, sales, business, revenue, business interruption, anticipated savings, opportunity, goodwill, reputation, or any indirect or consequential loss.</p>
      <p>9.5 <strong>Consumer users</strong>: we provide the Website for domestic and private use. You agree not to use it for commercial purposes, and we have no liability for any loss of profit, business, business interruption or business opportunity.</p>
      <p>9.6 We assume no responsibility for the content of linked websites.</p>
      <p>9.7 We are not liable for damage caused by viruses, DDoS attacks or other technologically harmful material that may infect your equipment due to your use of the Website or any downloaded content.</p>

      <h2>10. Viruses</h2>
      <p>10.1 We do not guarantee the Website will be secure or free from bugs or viruses.</p>
      <p>10.2 You are responsible for your own IT configuration and virus protection.</p>

      <h2>11. Privacy and Cookies</h2>
      <p>
        Our use of personal data is governed by our <a href="/legal/privacy">Privacy Policy</a> and,
        for US residents in applicable states, our US Privacy Notice. Our use of cookies is governed
        by our Cookie Notice.
      </p>

      <h2>12. Governing Law and Jurisdiction</h2>
      <p>12.1 These Website Terms are governed by <strong>English law</strong>.</p>
      <p>12.2 The courts of <strong>England and Wales</strong> have exclusive jurisdiction, except that:</p>
      <ul>
        <li>(a) consumers resident in Scotland or Northern Ireland may bring proceedings in their local courts;</li>
        <li>(b) consumers resident in the EEA, Switzerland or another jurisdiction giving consumers the right to sue locally (e.g., under Rome I Art. 6 or Brussels I (Recast)) retain that right; and</li>
        <li>(c) nothing in this clause limits <strong>mandatory</strong> consumer-protection laws of your country of residence.</li>
      </ul>

      <h2>13. US Users</h2>
      <p>
        If you are a US resident: (a) you and BlueRoam agree, to the maximum extent permitted by
        law, to resolve disputes individually and not as part of a class, consolidated or
        representative action (<strong>enforceability varies by state</strong>); and (b) mandatory
        federal or state consumer-protection law of your state of residence applies to the extent
        it cannot be contractually waived.
      </p>

      <h2>14. Contact</h2>
      <p>
        To contact us about these Website Terms:{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> or BlueRoam Ltd, 128 City Road,
        London, EC1V 2NX, United Kingdom.
      </p>
    </LegalPage>
  );
}
